import React, { useEffect } from 'react';
import { Card, Button, Space, Typography } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import { PageLoading } from '../common/LoadingStates';

const { Title, Paragraph } = Typography;

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * Authentication guard component that protects routes
 * Shows loading state while checking authentication
 * Shows login prompt if not authenticated
 * Renders children if authenticated
 */
const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { isAuthenticated, isLoading, loginRedirect } = useAuth();

  useEffect(() => {
    // Auto-redirect to login if not authenticated
    if (!isLoading && !isAuthenticated) {
      loginRedirect();
    }
  }, [isLoading, isAuthenticated, loginRedirect]);

  if (isLoading) {
    return <PageLoading message="Checking authentication..." />;
  }

  if (!isAuthenticated) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
          padding: '24px',
        }}
      >
        <Card
          style={{
            maxWidth: 500,
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          <LockOutlined
            style={{
              fontSize: 64,
              color: '#003366',
              marginBottom: 24,
            }}
          />
          <Title level={3}>Authentication Required</Title>
          <Paragraph style={{ color: '#8c8c8c', marginBottom: 32 }}>
            Please sign in with your Microsoft account to access the MJ Estimator application.
          </Paragraph>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Button
              type="primary"
              size="large"
              onClick={loginRedirect}
              style={{ width: '100%' }}
            >
              Sign In with Microsoft
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthGuard;
